/**
 * Shape of the naming fields a device carries. Declared structurally rather than importing
 * the service's `Device` type so this stays usable from templates and standalone components
 * without pulling in the whole API service.
 */
export interface DeviceNameFields {
    id?: string;
    name?: string | null;
    /** The processor's self-reported system name, refreshed on every check-in. */
    systemName?: string | null;
    /** Server-resolved display name. Wins when present so views never disagree. */
    displayName?: string | null;
    /** Set only when an admin renamed the device in the portal, which pins `name`. */
    nameSetByUserUtc?: string | null;
    model?: string | null;
    payload?: { systemName?: string | null } | null;
}

/**
 * Resolves the name to show for a device.
 *
 * Mirrors the server's DeviceNaming precedence — admin override > device-reported system name >
 * registration name > model > short id — for the cases where the server-resolved `displayName`
 * is not on the object yet (an older cached record, or a partial WebSocket event).
 *
 * Never falls back to the bare id: that is a raw GUID and reads as noise in a device list.
 */
export function deviceDisplayName(device: DeviceNameFields | null | undefined): string {
    if (!device) return 'Device';

    const trimmed = (value: string | null | undefined): string => (typeof value === 'string' ? value.trim() : '');

    // The server already applied the full precedence, so prefer its answer.
    const resolved = trimmed(device.displayName);
    if (resolved) return resolved;

    const name = trimmed(device.name);
    if (device.nameSetByUserUtc && name) return name;

    // Falls back to the raw payload for devices that have not checked in since the
    // systemName field was promoted to a stored column.
    const systemName = trimmed(device.systemName) || trimmed(device.payload?.systemName);
    if (systemName) return systemName;

    if (name) return name;

    const model = trimmed(device.model);
    if (model) return model;

    const shortId = (device.id ?? '').slice(0, 8);
    return shortId ? `Device ${shortId}` : 'Device';
}

/**
 * What the portal calls the processor a device hangs off. The data model already uses this
 * term (`ControlSystemDeviceId` on a location, `SystemName` on a device), so the UI matches it
 * rather than adding a fourth synonym.
 */
export const CONTROL_SYSTEM_LABEL = 'System';

/** Human label for a location node's `type`, used in headings and empty states. */
export function locationTypeLabel(type: string | null | undefined): string {
    switch ((type ?? '').trim().toLowerCase()) {
        case 'room':
            return 'Room';
        case 'floor':
            return 'Floor';
        case 'building':
            return 'Building';
        case 'rack':
            return 'Rack';
        default:
            return 'Location';
    }
}

/**
 * Name to show for a location. Never the id — a raw GUID in place of a room name reads as
 * corruption rather than as "this room was never named".
 */
export function locationDisplayName(name: string | null | undefined, type?: string | null): string {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (trimmed) return trimmed;
    return `Unnamed ${locationTypeLabel(type).toLowerCase()}`;
}

/**
 * Name to show for a location referenced only by id, where the record may not be loaded.
 *
 * Distinguishes the three cases the id-fallback used to collapse together: no location at all,
 * a location whose record is missing from the loaded set, and a location that genuinely has no
 * name. None of them render the id.
 */
export function locationRefName(name: string | null | undefined, locationId: string | null | undefined, type?: string | null): string {
    const trimmedName = typeof name === 'string' ? name.trim() : '';
    if (trimmedName) return trimmedName;
    if (!locationId) return '—';
    return `Unknown ${locationTypeLabel(type).toLowerCase()}`;
}

/** Naming fields carried by a linked (child) device. */
export interface LinkedDeviceNameFields {
    id?: string;
    uniqueId?: string | null;
    name?: string | null;
    modelName?: string | null;
    type?: string | null;
}

/**
 * Resolves the name to show for a linked device: its reported name, then its model, then its
 * type, then a short id-derived placeholder. Mirrors `deviceDisplayName` in never surfacing a
 * bare GUID.
 */
export function linkedDeviceDisplayName(device: LinkedDeviceNameFields | null | undefined): string {
    if (!device) return 'Linked device';

    const trimmed = (value: string | null | undefined): string => (typeof value === 'string' ? value.trim() : '');

    const name = trimmed(device.name);
    if (name && name !== 'Unknown') return name;

    const modelName = trimmed(device.modelName);
    if (modelName) return modelName;

    const type = trimmed(device.type);
    if (type && type !== 'Device') return type;

    const shortId = (trimmed(device.uniqueId) || trimmed(device.id)).slice(0, 8);
    return shortId ? `Linked device ${shortId}` : 'Linked device';
}

/**
 * Name to show for the control system a linked device or location belongs to, given a name the
 * API already resolved plus its id. Falls back to a short id-derived placeholder that still
 * reads as a control system, so the link never renders a bare GUID.
 */
export function controlSystemDisplayName(name: string | null | undefined, deviceId: string | null | undefined): string {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (trimmed) return trimmed;
    const shortId = (deviceId ?? '').slice(0, 8);
    return shortId ? `${CONTROL_SYSTEM_LABEL} ${shortId}` : CONTROL_SYSTEM_LABEL;
}
